import { EmptyState, Pagination, Table, type TableColumn } from '@today/ui-components'
import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { OfferStatusBadge } from './offer-status-badge'
import type { OfferRowView, Translate } from './offer-types'

/**
 * Props for {@link OfferListView}.
 *
 * @property rows - The current page, already mapped for display.
 * @property error - True when the last request failed: renders the retry state.
 * @property onCreate - Present only when the viewer may create offers (`offer.write`).
 */
export interface OfferListViewProps {
	t: Translate
	rows: OfferRowView[]
	page: number
	pageCount: number
	error: boolean
	onPageChange: (page: number) => void
	onOpen: (id: string) => void
	onRetry: () => void
	onCreate?: () => void
}

const columns = (t: Translate, onOpen: (id: string) => void): TableColumn<OfferRowView>[] => [
	{ key: 'createdAt', header: t('list.columns.created') },
	{ key: 'status', header: t('list.columns.status'), cell: (row) => <OfferStatusBadge t={t} status={row.status} /> },
	{
		key: 'open',
		header: t('list.columns.open'),
		cell: (row) => (
			<Button variant="muted" size="sm" onPress={() => onOpen(row.id)}>
				{t('list.open')}
			</Button>
		),
	},
]

/** The offer list: table, pagination, the New-offer entry, empty and error states (organism). */
export function OfferListView(props: OfferListViewProps): ReactElement {
	const { t } = props
	const body = props.error ? (
		<EmptyState title={t('list.error')} action={{ label: t('actions.retry'), onPress: props.onRetry }} />
	) : (
		<Table
			aria-label={t('list.title')}
			columns={columns(t, props.onOpen)}
			rows={props.rows}
			emptyState={<EmptyState title={t('list.empty')} />}
		/>
	)
	return (
		<section className="flex flex-col gap-4">
			<header className="flex items-center justify-between">
				<h1 className="text-ds-xl font-semibold text-ds-text">{t('list.title')}</h1>
				{props.onCreate ? <Button onPress={props.onCreate}>{t('list.new')}</Button> : null}
			</header>
			{body}
			<Pagination page={props.page} pageCount={props.pageCount} onPageChange={props.onPageChange} />
		</section>
	)
}
